import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { flushPersistedCache, persister, PERSIST_BUSTER, PERSIST_MAX_AGE, queryClient, shouldPersistQuery, suspendPersistence } from './api/queryClient';
import { AuthProvider } from './auth/AuthProvider';
import { AppErrorBoundary } from './components/RouteError';
import { ToastProvider } from './components/toast';
import { I18nProvider } from './i18n';
import { isIOS, isStandalone } from './lib/standalone';
import { startServiceWorker } from './offline/registerSW';
import { onLocalDataWipedElsewhere } from './offline/wipe';
import './styles.css';

startServiceWorker();

// Room below the status bar for iOS 26's edge blur (styles.css --edge-ramp).
if (isIOS() && isStandalone()) document.documentElement.dataset.iosApp = '';

// Persist the query cache immediately when the app goes to the background or is closed.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') void flushPersistedCache();
});
window.addEventListener('pagehide', () => void flushPersistedCache());

// Another tab of the app signed out or switched user (shared storage): this tab's queries belong to
// the previous session. Never write them back (the persisted cache is the other tab's now), drop
// them and start again from what is stored.
onLocalDataWipedElsewhere(() => {
  suspendPersistence();
  queryClient.clear();
  window.location.reload();
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nProvider>
      {/* Last resort: a crash in a provider shows a translated error screen, not a white page. */}
      <AppErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: PERSIST_MAX_AGE,
          buster: PERSIST_BUSTER,
          dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
        }}
      >
        <ToastProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ToastProvider>
      </PersistQueryClientProvider>
      </AppErrorBoundary>
    </I18nProvider>
  </StrictMode>,
);
